import { useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useBookings } from '@/hooks/useBookings'
import { useConfirm } from '@/hooks/useConfirm'
import toast from 'react-hot-toast'
import PageContainer from '@/components/layout/PageContainer'
import PageHeader from '@/components/layout/PageHeader'
import EmptyState from '@/components/ui/EmptyState'
import LoadingState from '@/components/ui/LoadingState'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import StatusBadge from '@/components/ui/StatusBadge'
import Breadcrumbs from '@/components/ui/Breadcrumbs'
import { CheckCircle, XCircle, User, Calendar, MapPin, Info, ClipboardCheck } from 'lucide-react'
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { notificationService } from '@/services/notificationService'

interface ApprovalQueuePageProps {
  embedded?: boolean
}

export default function ApprovalQueuePage({ embedded = false }: ApprovalQueuePageProps) {
  const { currentOrg } = useAuthStore()
  const { bookings, loading } = useBookings({
    orgId: currentOrg?.id,
    status: 'PENDING_APPROVAL',
    orderByField: 'createdAt',
    orderDirection: 'desc'
  })
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm()
  const [processingId, setProcessingId] = useState<string | null>(null)

  const handleApprove = async (bookingId: string, userId: string, spaceName: string) => {
    // ... logic remains same
    const confirmed = await confirm({
      title: 'Aprovar Reserva',
      description: 'Tem certeza que deseja aprovar esta reserva?',
      confirmText: 'Aprovar',
      variant: 'info'
    })

    if (!confirmed) return

    try {
      setProcessingId(bookingId)
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'CONFIRMED',
        updatedAt: serverTimestamp()
      })

      if (currentOrg) {
        await notificationService.create(
          userId,
          currentOrg.id,
          'booking_approved',
          'Reserva Aprovada',
          `Sua reserva para ${spaceName || 'o espaço'} foi aprovada!`,
          `/bookings/${bookingId}`
        )
      }

      toast.success('Reserva aprovada com sucesso!')
    } catch (error) {
      console.error('Error approving booking:', error)
      toast.error('Erro ao aprovar reserva')
    } finally {
      setProcessingId(null)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReject = async (bookingId: string, userId: string, spaceName: string) => {
    // ... logic remains same
    const confirmed = await confirm({
      title: 'Rejeitar Reserva',
      description: 'Tem certeza que deseja rejeitar esta reserva? Esta ação não pode ser desfeita.',
      confirmText: 'Rejeitar',
      variant: 'danger'
    })

    if (!confirmed) return

    try {
      setProcessingId(bookingId)
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'CANCELED',
        updatedAt: serverTimestamp()
      })

      if (currentOrg) {
        await notificationService.create(
          userId,
          currentOrg.id,
          'booking_rejected',
          'Reserva Rejeitada',
          `Sua reserva para ${spaceName || 'o espaço'} foi rejeitada.`,
          `/bookings/${bookingId}`
        )
      }

      toast.success('Reserva rejeitada')
    } catch (error) {
      console.error('Error rejecting booking:', error)
      toast.error('Erro ao rejeitar reserva')
    } finally {
      setProcessingId(null)
    }
  }

  if (!currentOrg) return null

  const Content = (
    <>
      {/* Info Banner */}
      <div className={`glass-card p-6 flex items-start gap-4 border-l-4 border-secondary ${embedded ? 'mt-4' : 'mt-10'}`}>
        <div className="p-2 bg-secondary/10 rounded-lg">
          <Info className="w-5 h-5 text-secondary shrink-0" />
        </div>
        <div>
          <h4 className="text-sm font-black text-primary uppercase tracking-tight">Sistema de Aprovação</h4>
          <p className="text-xs text-primary/70 mt-1 italic font-medium">
            Reservas aprovadas serão confirmadas imediatamente. Reservas rejeitadas serão canceladas e o usuário será notificado.
          </p>
        </div>
      </div>

      {/* Bookings List ... uses existing code below */}

      {/* Bookings List */}
      {loading ? (
        <div className="mt-12">
          <LoadingState />
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-12">
          <EmptyState
            icon={ClipboardCheck}
            title="Nenhuma aprovação pendente"
            description="Todas as reservas foram processadas. Você está em dia!"
          />
        </div>
      ) : (
        <div className="grid gap-6 mt-12">
          {bookings.map((booking: any) => (
            <div
              key={booking.id}
              className="glass-card p-6 group hover:-translate-y-1 transition-all duration-300 relative overflow-hidden border-b-4 border-b-transparent hover:border-b-secondary"
            >
              <div className="absolute -right-4 -top-4 w-28 h-28 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />

              <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-4 flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-black text-primary uppercase tracking-tight">
                        {booking.spaceName || 'Espaço'}
                      </h3>
                      <StatusBadge status={booking.status} className="mt-2" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 bg-primary/5 rounded-lg group-hover:bg-primary/10 transition-colors">
                        <User className="w-4 h-4 text-primary/80" />
                      </div>
                      <span className="text-primary/90 font-black uppercase text-[10px] tracking-widest">{booking.userName || 'Usuário'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 bg-primary/5 rounded-lg group-hover:bg-primary/10 transition-colors">
                        <Calendar className="w-4 h-4 text-primary/80" />
                      </div>
                      <span className="text-primary/90 font-black uppercase text-[10px] tracking-widest">
                        {booking.start?.toDate?.().toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 bg-primary/5 rounded-lg group-hover:bg-primary/10 transition-colors">
                        <MapPin className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-primary font-black text-xs italic tracking-tight">
                        R$ {booking.totalPrice?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleReject(booking.id, booking.userId, booking.spaceName)}
                    disabled={processingId === booking.id}
                    className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-red-200 text-red-600 font-bold rounded-xl hover:bg-red-50 transition-all uppercase text-sm tracking-wider disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    Rejeitar
                  </button>
                  <button
                    onClick={() => handleApprove(booking.id, booking.userId, booking.spaceName)}
                    disabled={processingId === booking.id}
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all uppercase text-sm tracking-wider shadow-lg shadow-primary/20 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aprovar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={options.title}
        description={options.description}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
        loading={processingId !== null}
      />
    </>
  )

  if (embedded) {
    return Content
  }

  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Aprovações' }]} />

      <PageHeader
        title="Fila de Aprovação"
        description={
          <>
            Aprove ou rejeite as reservas pendentes de <span className="text-primary font-bold">{currentOrg.name}</span>.
          </>
        }
      />
      {Content}
    </PageContainer>
  )
}
